import { SalesforceProduct } from '~/lib/salesforce/types';
import { CLOCK_SKEW_TOLERANCE_MS, SSR_PRODUCT_MAX_AGE_MS } from './constants';

export const productQueryKey = (productId: string | undefined) => [
    'Salesforce',
    'Product',
    productId,
];

/* `1` will cause a refetch on mount when passed to initialDataUpdatedAt. */
const FORCE_STALE_UPDATE = 1;

/**
 * Evaluate the correct `initialDataUpdatedAt` for React Query especially for requests
 * initially done server-side. SSR data are cached by our CDN, get stale but never refetched.
 */
export const getInitialDataUpdatedAt = (timestamp: number | undefined): number | undefined => {
    if (typeof timestamp !== 'number') return undefined;

    const now = Date.now();

    const isAheadOfOurClock = timestamp > now + CLOCK_SKEW_TOLERANCE_MS;
    const isTooOld = timestamp < now - SSR_PRODUCT_MAX_AGE_MS;

    // We will force the update if our timestamp is too old or desynced.
    return isAheadOfOurClock || isTooOld ? FORCE_STALE_UPDATE : timestamp;
};

export const sortIDsForQueryKeys = (list: number[] | string[]) =>
    [...list].sort((a, b) => {
        if (typeof a === 'number' && typeof b === 'number') {
            return a - b;
        }

        return String(a).localeCompare(String(b));
    });

// Builds a similar "key" using product IDs for variants of a product.
export const getVariantFamilyKey = (product: SalesforceProduct) => {
    const ids = product.c_variants?.map((variant) => variant.productId) ?? [];

    return ids.length ? (sortIDsForQueryKeys(ids) as string[]).join(',') : product.id;
};

export const scrollToElementById = (elementId: string) => {
    const element = document.getElementById(elementId);

    if (!element) return;

    // We will use the element's 'scroll-margin-top' if available.
    const scrollMargin = parseFloat(getComputedStyle(element).scrollMarginTop) || 0;

    const headerHeight =
        parseFloat(
            getComputedStyle(document.documentElement).getPropertyValue('--mainNavigation-height'),
        ) || 0;

    const offset = scrollMargin || headerHeight;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({
        top: element.getBoundingClientRect().top + window.scrollY - offset,
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });

    // Make element focusable if it cannot be focused.
    if (!element.hasAttribute('tabindex')) element.setAttribute('tabindex', '-1');
    // This will prevent jank when we switch the focus to the element.
    element.focus({ preventScroll: true });
};
